import maplibregl, { type Map as MapLibreMap, type Marker } from 'maplibre-gl';

/**
 * Names on the map.
 *
 * A field of coloured dots answers "where is the work" but not "who is that",
 * which is the question a dispatcher actually asks when the phone rings. The
 * incumbent labels its drivers, and so do we.
 *
 * Deliberately HTML markers rather than a MapLibre symbol layer. Symbol layers
 * render text from a glyph server, and our style has no `glyphs` key -- adding
 * one would mean either a second external dependency alongside the tiles, or
 * hosting font PBFs, to draw a dozen short names. Markers are plain DOM: they
 * cost nothing at this scale, they inherit the console's typography, and they
 * keep working on a blank canvas when no basemap loads at all.
 */

export interface MapLabel {
  key: string;
  text: string;
  lat: number;
  lng: number;
  /** Execution state, so a late technician reads as late here too. */
  tone?: 'ok' | 'warn' | 'late' | 'idle';
  onClick?: () => void;
}

/**
 * Reconcile a set of labels against the map.
 *
 * Keyed and patched rather than cleared and rebuilt: markers are DOM, and
 * removing every one of them on each board update would flicker the names and
 * drop any hover a dispatcher was in the middle of.
 */
export function syncLabels(
  map: MapLibreMap,
  existing: Map<string, Marker>,
  labels: MapLabel[],
): void {
  const wanted = new Set(labels.map((l) => l.key));

  for (const [key, marker] of existing) {
    if (!wanted.has(key)) {
      marker.remove();
      existing.delete(key);
    }
  }

  for (const label of labels) {
    let marker = existing.get(label.key);

    if (!marker) {
      const element = document.createElement('button');
      element.type = 'button';
      element.className = 'map-label';
      marker = new maplibregl.Marker({ element, anchor: 'left', offset: [10, 0] })
        .setLngLat([label.lng, label.lat])
        .addTo(map);
      existing.set(label.key, marker);
    } else {
      marker.setLngLat([label.lng, label.lat]);
    }

    const element = marker.getElement();
    if (element.textContent !== label.text) element.textContent = label.text;
    if (label.tone) element.dataset.tone = label.tone;
    else delete element.dataset.tone;

    element.onclick = label.onClick ? () => label.onClick?.() : null;
    element.style.cursor = label.onClick ? 'pointer' : 'default';
  }
}

/** Drop every marker, for component teardown. */
export function clearLabels(existing: Map<string, Marker>): void {
  for (const marker of existing.values()) marker.remove();
  existing.clear();
}
